/**
 * « Ma journée » QUAND L'ANCIEN PARCOURS EST EN PAUSE — chantier « manuel
 * vivant », Φ-MV-7 (revue UX `UX-7-03`, `UX-7-14` ; décision F-32 du fondateur).
 *
 * Deux produits se contredisaient sur le même élève : la file du matin
 * annonçait un « Blocage à traiter » après six essais du manuel faits le même
 * jour, pendant que sa fiche disait « En progression » ; et la carte portait un
 * code de savoir (`MAT-3-NB-SCI`). En pause (`ancienParcoursEnPause()`,
 * `lib/manuel-vivant.ts`), « Ma journée » ne dit plus que ce que le manuel dit :
 *
 *  - les unités de l'ANCIEN SUIVI qui parlent d'un élève ayant une fiche du
 *    manuel — « blocage à traiter », « élève à relancer » — ne sont plus
 *    affichées : le manuel a ses propres signaux pour cela (erreur récurrente,
 *    blocage en amont, inactivité), et c'est sa liste qui les porte ;
 *  - à leur place, LES ÉLÈVES À VOIR EN PRIORITÉ de `GET /v1/tuteur/manuel/eleves`,
 *    dans l'ordre du serveur, chacun avec la raison que le serveur écrit ;
 *  - ce qui reste de la file (un signal de sécurité, TOUJOURS ; une évaluation
 *    qui attend un verdict humain) s'ouvre sur la fiche du manuel, et ne porte
 *    aucun code interne.
 *
 * Rien n'est supprimé : le serveur calcule la file comme avant, et le réglage
 * remis à faux la rend telle quelle. Rien n'est rédigé ici qui dise où en est
 * l'élève : phrase et étiquette viennent du serveur.
 *
 * Fonctions pures : elles s'éprouvent sans rendu (`ma-journee-manuel.test.ts`).
 */
import type { ElevesSuivisResponse, TypeUniteTravail, UniteTravailDto } from '@silex/shared';

import { adressesManuelEleve } from './manuel-tuteur-format';
import { lireLigne } from './manuel-tuteur-lectures';

/** Ce que la liste « Mes élèves » dit d'un élève qui a une fiche du manuel. */
export interface RepereManuel {
  readonly eleveId: string;
  readonly prenom: string;
  /** La raison écrite par le serveur — « Pourquoi maintenant ». */
  readonly pourquoi: string;
  /** L'étiquette du signal qui l'emporte ; `null` : rien à signaler. */
  readonly etiquette: string | null;
  /** « À voir en priorité cette semaine » — décidé par le serveur. */
  readonly aVoirEnPriorite: boolean;
}

/**
 * Les élèves qui ont une fiche du manuel, par identifiant, DANS L'ORDRE DU
 * SERVEUR. Un élève sans donnée pédagogique (consentement absent) n'en a pas :
 * le serveur refuserait sa fiche, on ne l'y envoie donc pas.
 */
export function reperesDuManuel(reponse: ElevesSuivisResponse): ReadonlyMap<string, RepereManuel> {
  const reperes = new Map<string, RepereManuel>();
  for (const brut of reponse.eleves) {
    const ligne = lireLigne(brut);
    if (!ligne.suivi) continue;
    reperes.set(ligne.eleve.id, {
      eleveId: ligne.eleve.id,
      prenom: ligne.eleve.prenom,
      pourquoi: ligne.donnees.pourquoiMaintenant ?? '',
      etiquette: ligne.donnees.signal?.etiquette ?? null,
      aVoirEnPriorite: ligne.donnees.aVoirEnPriorite === true,
    });
  }
  return reperes;
}

/** Les élèves à voir en priorité, dans l'ordre reçu — rien n'est trié ici. */
export function aVoirEnPriorite(reperes: ReadonlyMap<string, RepereManuel>): RepereManuel[] {
  return [...reperes.values()].filter((r) => r.aVoirEnPriorite);
}

/**
 * Les types d'unité de l'ancien suivi que le manuel REDIT avec ses propres
 * signaux. Un signal de sécurité n'y figurera jamais : la sécurité d'un mineur
 * ne se met pas en pause. Une évaluation à valider non plus : un verdict qui
 * attend un humain ne contredit rien, et le cacher serait le perdre.
 */
export const TYPES_D_UNITE_EN_PAUSE: readonly TypeUniteTravail[] = [
  'blocage_savoir',
  'eleve_a_relancer',
];

/**
 * L'unité est-elle en pause ? Seulement pour un élève qui A une fiche du manuel
 * (`repere`) : sans elle — consentement absent, liste non lue — l'ancien suivi
 * reste la seule chose que le tuteur sache de lui.
 */
export function uniteEnPause(
  unite: Pick<UniteTravailDto, 'type'>,
  repere: RepereManuel | undefined,
): boolean {
  return repere !== undefined && TYPES_D_UNITE_EN_PAUSE.includes(unite.type);
}

/** Un code stable du référentiel : `MAT-3-NB-SCI`, `MAT-3-EXP-N2-E-PROD`, `FR-1-CO-03`. */
const CODE_INTERNE = /\b[A-Z]{2,4}-\d(?:-[A-Z0-9]+)+\b/g;

/** Le texte, sans aucun code interne — ni la ponctuation qu'il laisserait orpheline. */
export function sansCodeInterne(texte: string): string {
  return texte
    .replace(CODE_INTERNE, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s·—–-]+|[\s·—–-]+$/g, '');
}

/** Ce qu'une carte de la file affiche d'une unité restée à l'écran. */
export interface LectureDUnite {
  readonly motif: string;
  /** Les faits datés, sans celui qui n'était qu'un code. */
  readonly ancrage: readonly string[];
  /** Où mène « Ouvrir sa fiche ». */
  readonly fiche: string;
}

/** Une unité restée à l'écran, en pause : sans code interne, et ouverte sur la fiche du manuel quand elle existe. */
export function lireUnite(unite: UniteTravailDto, repere: RepereManuel | undefined): LectureDUnite {
  return {
    motif: sansCodeInterne(unite.motif),
    ancrage: unite.ancrage.map(sansCodeInterne).filter((fait) => fait !== ''),
    fiche:
      repere === undefined
        ? `/eleves/${unite.cible.eleveId}`
        : adressesManuelEleve.fiche(unite.cible.eleveId),
  };
}
